import React, { useMemo } from 'react';

import {
  BundleWork,
  DateRangeFilter,
} from 'js/bulk_download/work_cards_shared/types';
import { WorkId } from 'js/liswp/types';
import { Icon } from 'js/design_system/Icon';
import { useInBreakpoints } from 'js/design_system/hooks/useBreakpoint';

import { useResponsiveLayout } from 'js/bulk_download/work_cards_shared/useResponsiveLayout';
import { useModalAnalytics } from './useModalAnalytics';
import { ModalHeader } from './ModalHeader';
import { ModalFiltersSection } from './ModalFiltersSection';
import { ModalPreviewSection } from './ModalPreviewSection';
import { ModalFooter } from './ModalFooter';
import {
  StyledModal,
  CloseButton,
  Divider,
  ModalBody,
} from './StatelessWorkCardsUpsellModal.styles';

const BASIC_TIER_DEFAULT_PAPER_LIMIT = 6;
const AVERAGE_PRICE_PER_PAPER = 43;

export interface Props {
  open?: boolean;
  onClose(): void;
  onPrimaryCtaClicked(): void;
  bundleWorks: Array<BundleWork>;
  sourceWork: {
    id: WorkId;
    title: string;
  };
  /** Full preview data for the source work, rendered as the pinned "paper you are reading" card. */
  sourceWorkPreview?: BundleWork;
  loading: boolean;
  showAnnotations: boolean;
  topicQuery: string;
  onTopicQueryChange: (value: string) => void;
  dateRange: DateRangeFilter;
  onDateRangeChange: (value: DateRangeFilter) => void;
  selectedBuckets: Set<string>;
  onSelectAllBuckets: () => void;
  onToggleBucket: (bucket: string) => void;
  /**
   * Sticky set of available bucket names (captured from the initial unfiltered
   * load).  When provided, the category chips are rendered from this set
   * instead of being re-derived from the (potentially filtered) bundleWorks.
   */
  availableBuckets?: Set<string>;
  /**
   * Analytics identifier for this modal variant.
   */
  modalVariant: string;
  /**
   * Impression source for the filter panel.
   *
   * Required so that each modal variant emits a distinguishable impression.
   * ImpressionWatcher de-dupes by (source, entity_type, entity_id) and does
   * not include `extra`, so Premium and non-Premium MUST use different sources.
   */
  filterPanelImpressionSource: string;
  /**
   * Impression source for the primary CTA.
   *
   * Required for the same de-dupe reason as `filterPanelImpressionSource`.
   */
  primaryCtaImpressionSource: string;
  /**
   * Impression source for the preview paper list.
   *
   * Required for the same de-dupe reason as `filterPanelImpressionSource`.
   */
  previewImpressionSource: string;
  /** Whether this is the premium variant. Affects header copy, CTA labels, etc. */
  isPremium?: boolean;
  /** Custom primary CTA label. Defaults based on isPremium. */
  primaryCtaLabel?: string;
  /**
   * Number of papers to show before fade/blur. If undefined, shows all papers.
   * Basic variant uses 6, Premium shows all.
   */
  paperLimit?: number;
  /** Custom secondary action handler. If not provided, shows default "No thanks" link. */
  onSecondaryAction?: () => void;
  /** Custom secondary action label. Used with onSecondaryAction. */
  secondaryActionLabel?: string;
  /** Whether to hide the secondary action entirely (for premium downloading state). */
  hideSecondaryAction?: boolean;
  /**
   * When true, the filter sidebar ("Customize PDF Pack") is hidden entirely and
   * the paper preview list takes the full width.
   */
  hideFilters?: boolean;
  /** Transactional pricing A/B test bucket ('variant' | 'control') for tracking. */
  transactionalPricingBucket?: string;
  /**
   * Whether to show the publisher-site dollar-value banner above the paper
   * list. Controlled by the bulk_download_2026_q1_value_for_dollar A/B test.
   */
  showValueBanner?: boolean;
}

function StatelessWorkCardsUpsellModal({
  open = true,
  onClose,
  onPrimaryCtaClicked,
  bundleWorks,
  sourceWork,
  sourceWorkPreview,
  loading,
  showAnnotations,
  topicQuery,
  onTopicQueryChange,
  dateRange,
  onDateRangeChange,
  selectedBuckets,
  onSelectAllBuckets,
  onToggleBucket,
  availableBuckets: availableBucketsProp,
  modalVariant,
  filterPanelImpressionSource,
  primaryCtaImpressionSource,
  previewImpressionSource,
  isPremium = false,
  primaryCtaLabel: customPrimaryCtaLabel,
  paperLimit,
  onSecondaryAction,
  secondaryActionLabel,
  hideSecondaryAction = false,
  hideFilters = false,
  transactionalPricingBucket,
  showValueBanner: showValueBannerProp = false,
}: Props) {
  // ── Responsive layout ──────────────────────────────────────────────────
  const { isMobile, filtersExpanded, setFiltersExpanded } = useResponsiveLayout(
    { mobileBreakpoint: 567 },
  );
  const isNotLarge = useInBreakpoints(['small', 'medium']);

  // ── Derived data ───────────────────────────────────────────────────────
  const { relatedWorks, availableBuckets } = useMemo(() => {
    const works = bundleWorks ?? [];
    const related = works.filter((w) => w.bucket !== 'thePaperItself');
    const bucketsInResponse = new Set<string>();
    related.forEach((w) => {
      if (w.bucket) bucketsInResponse.add(w.bucket);
    });
    return {
      relatedWorks: related,
      availableBuckets: availableBucketsProp ?? bucketsInResponse,
    };
  }, [bundleWorks, availableBucketsProp]);

  const totalRelatedCount = relatedWorks.length;
  const relatedCountLabel =
    totalRelatedCount === 0
      ? null
      : totalRelatedCount >= 37
        ? '37+'
        : String(totalRelatedCount);

  const hasActiveFilters =
    topicQuery.trim().length > 0 ||
    dateRange !== 'all' ||
    selectedBuckets.size > 0;

  const primaryCtaLabel = customPrimaryCtaLabel
    ? customPrimaryCtaLabel
    : isPremium
      ? 'Download PDF Pack'
      : 'Download PDF Pack for $1';

  const basicTierPaperLimit = paperLimit ?? BASIC_TIER_DEFAULT_PAPER_LIMIT;
  const visibleRelatedWorks = isPremium
    ? relatedWorks
    : relatedWorks.slice(0, basicTierPaperLimit);

  const hiddenPapersCount = totalRelatedCount - visibleRelatedWorks.length;
  const showFadeFooter = !isPremium && hiddenPapersCount > 0;

  // ── Value banner ─────────────────────────────────────────────────────
  const totalPaperCount = totalRelatedCount + (sourceWorkPreview ? 1 : 0);
  const publisherSiteValue = totalPaperCount * AVERAGE_PRICE_PER_PAPER;
  const showValueBanner =
    showValueBannerProp && !isPremium && totalPaperCount > 0;

  // ── Analytics ──────────────────────────────────────────────────────────
  const { trackFilterClick, trackPreviewImpression, buildTrackClick } =
    useModalAnalytics({
      sourceWorkId: sourceWork.id,
      isPremium,
      modalVariant,
      topicQuery,
      dateRange,
      selectedBuckets,
      relatedWorksCount: relatedWorks.length,
      hasActiveFilters,
      loading,
    });

  const handleToggleFilters = (opening: boolean) => {
    trackFilterClick({ filterValue: opening ? 'open' : 'close' });
    setFiltersExpanded(opening);
  };

  // ── Render ─────────────────────────────────────────────────────────────
  const showContent = !(isMobile && filtersExpanded && !hideFilters);

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      showHeader={false}
      containerStyle={{ width: 'min(95vw, 1000px)' }}
    >
      <CloseButton type="button" onClick={onClose} aria-label="Close">
        <Icon name="close" size={24} />
      </CloseButton>

      <ModalHeader
        modalTitle={'Download your PDF Pack'}
        relatedCountLabel={relatedCountLabel}
      />

      <Divider />

      <ModalBody
        className={filtersExpanded && !hideFilters ? 'expanded' : 'collapsed'}
      >
        {!hideFilters && (isMobile || filtersExpanded) && (
          <ModalFiltersSection
            isMobile={isMobile}
            filtersExpanded={filtersExpanded}
            onToggleFilters={handleToggleFilters}
            filterPanelImpressionSource={filterPanelImpressionSource}
            sourceWorkId={sourceWork.id}
            modalVariant={modalVariant}
            isPremium={isPremium}
            topicQuery={topicQuery}
            onTopicQueryChange={onTopicQueryChange}
            dateRange={dateRange}
            onDateRangeChange={onDateRangeChange}
            selectedBuckets={selectedBuckets}
            onSelectAllBuckets={onSelectAllBuckets}
            onToggleBucket={onToggleBucket}
            availableBuckets={availableBuckets}
            buildTrackClick={buildTrackClick}
          />
        )}

        {showContent && (
          <ModalPreviewSection
            loading={loading}
            hideFilters={hideFilters}
            filtersExpanded={filtersExpanded}
            isMobile={isMobile}
            badgePlacement={isNotLarge ? 'above' : 'side'}
            onExpandFilters={() => handleToggleFilters(true)}
            sourceWorkId={sourceWork.id}
            sourceWorkPreview={sourceWorkPreview}
            modalVariant={modalVariant}
            isPremium={isPremium}
            showAnnotations={showAnnotations}
            visibleRelatedWorks={visibleRelatedWorks}
            totalRelatedCount={totalRelatedCount}
            showFadeFooter={showFadeFooter}
            hiddenPapersCount={hiddenPapersCount}
            hasActiveFilters={hasActiveFilters}
            previewImpressionSource={previewImpressionSource}
            onPreviewImpression={trackPreviewImpression}
            showValueBanner={showValueBanner}
            publisherSiteValue={publisherSiteValue}
            totalPaperCount={totalPaperCount}
            averagePricePerPaper={AVERAGE_PRICE_PER_PAPER}
          />
        )}
      </ModalBody>

      {showContent && (
        <ModalFooter
          isPremium={isPremium}
          hideSecondaryAction={hideSecondaryAction}
          onSecondaryAction={onSecondaryAction}
          onClose={onClose}
          secondaryActionLabel={secondaryActionLabel}
          onPrimaryCtaClicked={onPrimaryCtaClicked}
          primaryCtaLabel={primaryCtaLabel}
          primaryCtaImpressionSource={primaryCtaImpressionSource}
          sourceWorkId={sourceWork.id}
          modalVariant={modalVariant}
          transactionalPricingBucket={transactionalPricingBucket}
        />
      )}
    </StyledModal>
  );
}

export default StatelessWorkCardsUpsellModal;
